import { TFunction } from 'i18next'
import { v4 as uuid } from 'uuid'
import { getEncounterAbilityCustomGrouping } from 'app/frontend/types/custom/EncounterAbilityAssignmentDefaults'
import {
  RaidPlanAssignmentEntity,
  RaidPlanEncounterAssignmentAbility,
  RaidPlanEncounterAssignmentAbilityKey,
  RaidPlanEncounterAssignmentBox,
  RaidPlanEncounterAssignmentCast,
  RaidPlanTimeline,
} from 'app/frontend/types/custom/RaidPlan'

export type AssignmentViewMode = 'all_together' | 'alternating' | 'all_separate'

export type AssignmentScope = {
  id: string
  label: string
  castIndexes: number[]
  castGroups: number[][]
}

export type AssignmentScopeIndexes = Omit<AssignmentScope, 'label'>

const customGroupingRegex = /^\d+( \d+)*$/

export const getSoakPrefix = (label: string) =>
  label.trim().startsWith('Soak ') ? '' : 'Soak '

export const sanitizeCustomGroupingInput = (value: string) =>
  value
    .replace(/[^\d ]/g, '')
    .replace(/\s+/g, ' ')
    .replace(/^ /, '')

export const normalizeCustomGrouping = (value?: string | null) => {
  if (!value) return undefined
  const normalized = value.replace(/\s+/g, ' ').trim()
  if (!normalized || !customGroupingRegex.test(normalized)) return undefined
  const parsed = normalized.split(' ').map((token) => Number(token))
  if (parsed.some((size) => !Number.isInteger(size) || size < 1)) {
    return undefined
  }
  return parsed.join(' ')
}

export const parseCustomGrouping = (value?: string | null) => {
  const normalized = normalizeCustomGrouping(value)
  if (!normalized) return null
  return normalized.split(' ').map((token) => Number(token))
}

export const getEffectiveAbilityCustomGrouping = (
  spellId: number,
  customGrouping?: string,
) =>
  normalizeCustomGrouping(customGrouping) ??
  normalizeCustomGrouping(getEncounterAbilityCustomGrouping(spellId))

export const getEffectiveAssignmentViewMode = (viewMode?: AssignmentViewMode) =>
  viewMode ?? 'alternating'

export const getEffectiveAssignmentGroups = (
  defaultAssignmentGroups: number,
  assignmentGroups?: number,
) => {
  if (
    typeof assignmentGroups !== 'number' ||
    !Number.isFinite(assignmentGroups)
  ) {
    return Math.min(Math.max(defaultAssignmentGroups, 1), 20)
  }
  return Math.min(Math.max(Math.floor(assignmentGroups), 1), 20)
}

const normalizeAssignmentEntity = (
  entity: RaidPlanAssignmentEntity,
): RaidPlanAssignmentEntity => ({
  ...entity,
  id: entity.id ?? uuid(),
})

export const normalizeAssignmentBox = (
  box: RaidPlanEncounterAssignmentBox | undefined,
): RaidPlanEncounterAssignmentBox => ({
  id: box?.id ?? uuid(),
  entities: (box?.entities ?? []).map((entity) =>
    normalizeAssignmentEntity(entity),
  ),
})

export const normalizeAssignmentCast = (
  cast: RaidPlanEncounterAssignmentCast | undefined,
  assignmentGroups: number,
): RaidPlanEncounterAssignmentCast => ({
  id: cast?.id ?? uuid(),
  boxes: Array.from({ length: Math.max(assignmentGroups, 1) }, (_, boxIndex) =>
    normalizeAssignmentBox(cast?.boxes?.[boxIndex]),
  ),
})

export const getEncounterAssignmentAbilities = (
  timeline: RaidPlanTimeline,
): RaidPlanEncounterAssignmentAbility[] => {
  const abilitiesByKey = timeline.encounter_assignments?.abilities ?? {}
  const abilityOrder = timeline.encounter_assignments?.ability_order ?? []
  const ordered = abilityOrder
    .map((abilityKey) => abilitiesByKey[abilityKey])
    .filter((ability): ability is RaidPlanEncounterAssignmentAbility =>
      Boolean(ability),
    )
  const orderedKeySet = new Set<RaidPlanEncounterAssignmentAbilityKey>(
    abilityOrder,
  )
  const missing = Object.entries(abilitiesByKey)
    .filter(([abilityKey]) => !orderedKeySet.has(abilityKey))
    .map(([, ability]) => ability)
  return [...ordered, ...missing]
}

export const getAssignmentScopeIndexes = (
  entries: { time: number }[],
  viewMode: AssignmentViewMode,
  customGrouping?: string,
): AssignmentScopeIndexes[] => {
  if (!entries.length) return []
  const hasCustomGrouping = Boolean(parseCustomGrouping(customGrouping))
  const castGroups = buildAssignmentCastGroups(entries, customGrouping)
  if (viewMode === 'all_together') {
    return [
      {
        id: 'all',
        castIndexes: entries.map((_, index) => index),
        castGroups,
      },
    ]
  }
  if (viewMode === 'alternating') {
    const oddCastGroups = castGroups.filter((_, index) => index % 2 === 0)
    const evenCastGroups = castGroups.filter((_, index) => index % 2 === 1)
    return [
      {
        id: 'odd',
        castIndexes: oddCastGroups.flat(),
        castGroups: oddCastGroups,
      },
      {
        id: 'even',
        castIndexes: evenCastGroups.flat(),
        castGroups: evenCastGroups,
      },
    ].filter((scope) => scope.castIndexes.length > 0)
  }
  return castGroups.map((castGroup, index) => ({
    id: hasCustomGrouping ? `group-${index}` : `cast-${index}`,
    castIndexes: castGroup,
    castGroups: [castGroup],
  }))
}

export const getAssignmentScopes = (
  entries: { time: number }[],
  viewMode: AssignmentViewMode,
  t: TFunction,
  customGrouping?: string,
): AssignmentScope[] => {
  const scopes = getAssignmentScopeIndexes(entries, viewMode, customGrouping)
  const hasCustomGrouping = Boolean(parseCustomGrouping(customGrouping))
  return scopes.map((scope, scopeIndex) => {
    if (scope.id === 'all') {
      return {
        ...scope,
        label: t('dashboard.team.plans.assignments.view-modes.casts'),
      }
    }
    if (scope.id === 'odd') {
      return {
        ...scope,
        label: t('dashboard.team.plans.assignments.view-modes.odd'),
      }
    }
    if (scope.id === 'even') {
      return {
        ...scope,
        label: t('dashboard.team.plans.assignments.view-modes.even'),
      }
    }
    if (hasCustomGrouping) {
      return {
        ...scope,
        label: t('dashboard.team.plans.assignments.group-label', {
          count: scopeIndex + 1,
        }),
      }
    }
    return {
      ...scope,
      label: t('dashboard.team.plans.assignments.cast-label', {
        count: scopeIndex + 1,
      }),
    }
  })
}

const buildAssignmentCastGroups = (
  entries: { time: number }[],
  customGrouping?: string,
) => {
  const grouping = parseCustomGrouping(customGrouping)
  if (!grouping) {
    return entries.map((_, index) => [index])
  }
  const castGroups: number[][] = []
  let castIndex = 0
  let groupIndex = 0
  while (castIndex < entries.length) {
    const groupSize = grouping[groupIndex % grouping.length]
    const nextCastIndex = Math.min(castIndex + groupSize, entries.length)
    castGroups.push(
      Array.from(
        { length: nextCastIndex - castIndex },
        (_, index) => castIndex + index,
      ),
    )
    castIndex = nextCastIndex
    groupIndex += 1
  }
  return castGroups
}
